import { defineMessage, defineMessages } from 'react-intl'

export const requiredFieldErrorMessage = defineMessage({
  defaultMessage: 'Required',
  description: 'Error message displayed under input fields that require a non-empty value',
})

export const thisFieldIsRequiredErrorMessage = defineMessage({
  defaultMessage: 'This field is required',
  description: 'Text that says that field is required',
})

export const requiredSelectionErrorMessage = defineMessage({
  defaultMessage: 'A selection is required. Choose at least one option.',
  description: 'Error message displayed when no selection is made from a required selection field',
})

export const atLeastOneFieldRequiredErrorMessage = defineMessage({
  defaultMessage: 'At least one of {fieldNames} is required',
  description:
    'Error message displayed when at least one field in a group must have a value. fieldNames is a comma-separated list of field titles.',
})

export const mustBeStringErrorMessage = defineMessage({
  defaultMessage: 'Must be a string',
  description: 'Error message for value that is not a string',
})

export const overLimitCharactersErrorMessage = defineMessage({
  defaultMessage: 'Must be {max} characters or less',
  description: 'Error message for over limit characters in string',
})

export const enterAValidEmailErrorMessage = defineMessage({
  defaultMessage: 'Please enter a valid email address',
  description: 'Error message for invalid email address input',
})

export const emailRequiredForNotificationErrorMessage = defineMessage({
  defaultMessage: 'Email is required for this notification.',
  description: 'Error message for when email is not selected for a notification',
})

export const emailAddressMustMatchErrorMessage = defineMessage({
  defaultMessage: 'Email address must match',
  description: 'Error message for confirm email address input not matching',
})

export const enterANewEmailAddressErrorMessage = defineMessage({
  defaultMessage: 'Enter a new email address',
  description: 'Error message for entering the same email address as the current one',
})

export const enterAValidDateErrorMessage = defineMessage({
  defaultMessage: 'Please enter a valid date',
  description: 'Error message for entering a valid date input',
})

export const dateCannotBeInFutureError = defineMessage({
  defaultMessage: 'Date cannot be in the future',
  description: 'Error message for invalid future date',
})

export const invalidPhoneNumberError = defineMessage({
  defaultMessage: 'Invalid phone number',
  description: 'Error message for invalid phone number',
})

export const enterAValidPhoneNumberError = defineMessage({
  defaultMessage: 'Please enter a valid phone number',
  description: 'Error message for entering a valid phone number',
})

export const unsupportedCountryCodeErrorMessage = defineMessage({
  defaultMessage: 'Please select a valid country code',
  description: 'Error message when an unsupported country is selected for phone number entry',
})

export const requiredFieldErrorMessageWithFieldName = defineMessage({
  defaultMessage: 'This field is required: {fieldName}',
  description: 'Specifies which input field is required',
})

export const invalidPasswordErrorMessage = defineMessage({
  defaultMessage: 'Please create a stronger password!',
  description: 'Error message displayed under password input fields that require a stronger password',
})

export const invalidConfirmMatchingPasswordErrorMessage = defineMessage({
  defaultMessage: 'Please confirm that your password matches!',
  description: 'Text informing the user that the new password and the confirm password fields do not match',
})

export const invalidNumberErrorMessage = defineMessage({
  defaultMessage: 'Please enter a valid number',
  description: 'Error message when number input is invalid',
})

export const mustBeANumberErrorMessage = defineMessage({
  defaultMessage: 'Must be a number',
  description: 'Error message when value must be a number',
})

export const maxNumErrorMessage = defineMessage({
  defaultMessage: 'Must be {max} or less',
  description: 'Error message when value must be less than a number',
})

export const invalidYearErrorMessage = defineMessage({
  defaultMessage: 'Please enter a valid year',
  description: 'Error message when year is invalid',
})

export const yearCannotBeInFutureErrorMessage = defineMessage({
  defaultMessage: 'Year cannot be in the future',
  description: 'Error message when value is an invalid future year',
})

export const invalidDateErrorMessage = defineMessage({
  defaultMessage: 'Invalid Date',
  description: 'Error message when date is invalid',
})

export const invalidDateOfBirthErrorMessage = defineMessage({
  defaultMessage: 'Invalid date of birth',
  description: 'Error message when date of birth input is invalid',
})

export const mustBeOlderErrorMessage = defineMessage({
  defaultMessage: 'Must be {min} or older',
  description: 'Error message when user must be older',
})

export const mustBeYoungerErrorMessage = defineMessage({
  defaultMessage: 'Must be {max} or younger',
  description: 'Error message when user must be younger',
})

export const programOnlyAvailableForTeensErrorMessage = defineMessage({
  defaultMessage: 'This program is only available for teens ages 13-17',
  description: 'Error message when the age is outside the 13-17 teen range for a teen-only program',
})

export const invalidGivenNameErrorMessage = defineMessage({
  defaultMessage: 'Invalid characters in given name',
  description: 'Error message for invalid characters in given name',
})

export const invalidFirstNameErrorMessage = defineMessage({
  defaultMessage: 'Invalid characters in first name',
  description: 'Error message for invalid characters in first name',
})

export const invalidFamilyNameErrorMessage = defineMessage({
  defaultMessage: 'Invalid characters in surname / family name',
  description: 'Error message for invalid characters in family name',
})

export const invalidLastNameErrorMessage = defineMessage({
  defaultMessage: 'Invalid characters in last name',
  description: 'Error message for invalid characters in last name',
})

export const invalidZipCodeErrorMessage = defineMessage({
  defaultMessage: 'Please enter a valid 5 digit zip code',
  description: 'Error message for invalid zip code',
})

export const thisFieldRequiresResponse = defineMessage({
  defaultMessage: 'A response is required',
  description: 'Error message displayed under input fields that require a response',
})

export const invalidFormatValidationError = defineMessage({
  defaultMessage: 'Invalid format',
  description: 'Validation error message when user input has invalid format',
})

export const childSearchMessages = defineMessages({
  duplicatedChildInputValidationMessage: {
    defaultMessage: 'This child already exists in the system',
    description: 'Error message when a duplicated child is being created by the client.',
  },
  createChildUserFailureMessage: {
    defaultMessage: 'We’re sorry. There was an error in saving your child’s information. Please try again.',
    description: 'Error message shown in the banner when saving user’s child’s information has failed',
  },
  childEligibilityCheckFailureMessage: {
    defaultMessage:
      'It looks like your child’s name isn’t currently in our system. Check the organization that grants them access to Lyra to make sure their name and date of birth matches the information shared.',
    description: 'Error message shown when creating a child is unsuccessful due to an eligibility check failure',
  },
})

export const closeModalButtonLabel = defineMessage({
  defaultMessage: 'Close',
  description: 'Label of button to close a modal',
})

export const modalDialogAriaLabel = defineMessage({
  defaultMessage: 'Modal dialog',
  description: 'Accessibility label for modal dialog containers',
})

export const showPasswordButtonLabel = defineMessage({
  defaultMessage: 'Show password',
  description: 'Accessibility label for button to show password',
})

export const hidePasswordButtonLabel = defineMessage({
  defaultMessage: 'Hide password',
  description: 'Accessibility label for button to hide password',
})

export const infoButtonLabel = defineMessage({
  defaultMessage: 'More information',
  description: 'Accessibility label for info button',
})

export const removeSelectionButtonLabel = defineMessage({
  defaultMessage: 'Remove selection',
  description: 'Accessibility label for button to remove a selected item',
})

export const clearSearchButtonLabel = defineMessage({
  defaultMessage: 'Clear search',
  description: 'Accessibility label for button to clear search input',
})

export const resetPasswordErrorMessage = defineMessage({
  defaultMessage: 'We’re sorry there was an error requesting your reset password link, please try again',
  description: 'Error message when requesting a password reset link fails',
})

export const expiredAccountErrorMessage = defineMessage({
  defaultMessage: 'This account has expired, please use a different email',
  description: 'Error message when requesting a password reset link for an expired account',
})

export const customerCodeValidationError = defineMessage({
  defaultMessage: 'We cannot confirm this number',
  description: 'Validation error message for the input field of an employee ID number',
})

export const invalidEligibilityFirstNameMessage = defineMessage({
  defaultMessage: 'We cannot confirm this first name',
  description: 'Validation error message when inputted birthdate fails the eligibility check',
})

export const invalidEligibilityLastNameMessage = defineMessage({
  defaultMessage: 'We cannot confirm this last name',
  description: 'Validation error message when inputted birthdate fails the eligibility check',
})

export const invalidEligibilityDobMessage = defineMessage({
  defaultMessage: 'We cannot confirm this birthdate',
  description: 'Validation error message when inputted birthdate fails the eligibility check',
})

export const LOGIN_ERROR_CODES = {
  APPLE: 'APPLE',
  GENERIC: 'GENERIC',
  INCORRECT_LOGIN: 'INCORRECT_LOGIN',
  LOCKED: 'LOCKED',
  SSO_LOGIN_EMAIL_CONFLICT: 'SSO_LOGIN_EMAIL_CONFLICT',
  PARTNER_SSO_TOKEN_EXPIRED: 'PARTNER_SSO_TOKEN_EXPIRED',
} as const

export const loginErrorMessages = defineMessages({
  [LOGIN_ERROR_CODES.APPLE]: {
    defaultMessage: 'Sign in with Apple not supported.',
    description: 'Error message when user fails to login with apple',
  },
  [LOGIN_ERROR_CODES.GENERIC]: {
    defaultMessage:
      'Login could not be completed. Please try again later or <careTeamLink>contact the care team</careTeamLink> for help.',
    description: 'Error message when user fails to login',
  },
  [LOGIN_ERROR_CODES.INCORRECT_LOGIN]: {
    defaultMessage:
      "Looks like your email or password aren't in our system. Try adding them again, or reset your password.",
    description: 'Error message when user attempts to login with an incorrect username or password',
  },
  [LOGIN_ERROR_CODES.LOCKED]: {
    defaultMessage: 'This account may be locked, or may not exist. Please try to reset your password.',
    description: 'Error message when user attempts to login with a locked account',
  },
  [LOGIN_ERROR_CODES.SSO_LOGIN_EMAIL_CONFLICT]: {
    defaultMessage: 'Could not sign in. If you have an existing account, please log in below.',
    description:
      'Error message shown when user attempts to log in to their account with a different sign-on provider than expected. Prompts user to try again on the log in screen.',
  },
  [LOGIN_ERROR_CODES.PARTNER_SSO_TOKEN_EXPIRED]: {
    defaultMessage: 'Session expired. Please re-authenticate to complete your account setup.',
    description:
      'Error message shown on the login page when the partner SSO account verification link has expired. Prompts user to sign in again to finish setting up their account.',
  },
})

export const genericErrorMessage = defineMessage({
  defaultMessage: 'Sorry, something went wrong. Please try again.',
  description: 'Generic error message implying something went wrong.',
})

export const formErrorWithSupportMessage = defineMessage({
  defaultMessage:
    'Something went wrong. Please validate your entries and try again. If you need support, call {phoneNumber}.',
  description:
    'Form submission error message shown when something goes wrong, prompting the user to validate their entries and providing a support phone number.',
})

export const resetPasswordMessages = defineMessages({
  SUCCESS: {
    defaultMessage: 'Your password has successfully been changed. Log in using your new password.',
    description: 'Toast text informing user that their password has successfully been changed',
  },
  SUCCESS_LOGGED_IN: {
    defaultMessage: 'Your password has successfully been changed.',
    description: 'Toast text informing user that their password has successfully been changed',
  },
  INVALID_LINK: {
    defaultMessage: 'The link for resetting password is no longer valid. Please try resetting again.',
    description: 'Error message when the link user is using to reset password is no longer valid',
  },
})

export const creditCardErrorMessages = defineMessages({
  CARD_FIELD_INVALID: {
    defaultMessage: 'Your {cardFieldName} is invalid.',
    description: 'Validation error message when the credit card field is invalid',
  },
  CARD_FIELD_INCOMPLETE: {
    defaultMessage: 'Your {cardFieldName} is incomplete.',
    description: 'Validation error message when the credit card field is incomplete',
  },
  GENERIC_ERROR: {
    defaultMessage: 'Card information is invalid.',
    description: 'Validation error message when there card failed with unknown error',
  },
})

export const registrationMessages = defineMessages({
  GET_STARTED: {
    defaultMessage: 'Get started',
    description: 'Button text for patient to sign up to get care',
  },
  SIGN_UP_AND_EXPLORE: {
    defaultMessage: 'Sign up and explore',
    description: 'Button text for patient to sign up and explore Lyra offerings',
  },
})

export const existingChildProfileErrorMessage = defineMessage({
  defaultMessage:
    'We found an existing profile for this child. Select their profile to continue setting up care: <link>Find profile</link>.',
  description: 'Error message when a duplicated child is being created by the client',
})

export const careConsentFormError = defineMessage({
  defaultMessage: 'Agree to the terms of the program',
  description: 'Error message when client has not properly filled out the consent for care form',
})

export const dependentEligibilityCheckFailureMessage = defineMessage({
  defaultMessage:
    'It looks like your dependent’s name isn’t currently in our system. Check the organization that grants them access to Lyra to make sure their name and date of birth matches the information shared.',
  description: 'Error message shown when creating a dependent is unsuccessful due to an eligibility check failure',
})

export const registrationAndLoginDisabledMessage = defineMessage({
  defaultMessage:
    'To connect to care please contact our Care Navigator Team at <link></link> or <emailLink>care@lyrahealth.com</emailLink> and they will be able to assist you.',
  description: 'Text in a banner informing the user to contact a support specialist for assistance',
})

export const consentToSMSMessage = defineMessage({
  defaultMessage:
    'I agree to allow Lyra to contact me by phone or text at the phone number above with appointment reminders and other alerts.',
  description: 'Message prompting the user to agree to receiving notifications through SMS',
})

export const consentToSMSMessageForGuardianLedBooking = defineMessage({
  defaultMessage: 'Get text and email notifications about sessions and other updates about {childName}’s care.',
  description:
    'Message prompting the user to agree to receiving notifications through SMS when booking care for a teen',
})

export const smsConsentTooltipMessage = defineMessage({
  defaultMessage:
    'By providing your phone number, you give consent for Lyra to contact you about your mental health, treatment, counseling, billing, and account issues. We may also use this number to make sure you’re safe if you indicate you need urgent support.',
  description: 'Tooltip text informing user how their phone number will be used.',
})

export const invalidRegistrationOktaPassword = defineMessage({
  defaultMessage: 'Please try again. Your password cannot contain parts of your email.',
  description: 'Error message when user tries to sign up with password that contains part of username',
})

export const userRegisteredErrorMessage = defineMessage({
  defaultMessage:
    'This account has already registered. Please use different email address or <link>click here to login</link>.',
  description: 'Error message when user is finishing registration of an account which has already been registered',
})

export const codeFieldLabel = defineMessage({
  defaultMessage: 'Your code',
  description: 'Label for input field to enter a code',
})

export const enterAValidCodeErrorMessage = defineMessage({
  defaultMessage: 'Please enter a valid code',
  description: 'Error message for invalid code input',
})

export const codeResentMessage = defineMessage({
  defaultMessage: 'Your code has been resent',
  description: 'Message informing the user that a code has been resent',
})

export const backAccessibilityLabel = defineMessage({
  defaultMessage: 'Back',
  description: 'Accessibility label for navigation back button',
})

export const closeAccessibilityLabel = defineMessage({
  defaultMessage: 'Close',
  description: 'Accessibility label for close button',
})

export const codeRedirectErrorMessage = defineMessage({
  defaultMessage: 'For your security, enter your email to get a new code.',
  description: 'Message informing the user to enter their email address to request a new code',
})

export const nextItemLabel = defineMessage({
  defaultMessage: 'Next item',
  description: 'Accessibility label for next item in a carousel',
})

export const prevItemLabel = defineMessage({
  defaultMessage: 'Previous item',
  description: 'Accessibility label for previous item in a carousel',
})

export const pauseAudioLabel = defineMessage({
  defaultMessage: 'Pause audio',
  description: 'Accessibility label for pause button',
})

export const playAudioLabel = defineMessage({
  defaultMessage: 'Play audio',
  description: 'Accessibility label for play button',
})

export const emailUpdatedMessage = defineMessage({
  defaultMessage: 'Your email has been updated',
  description: 'Message informing the user that their email has been updated',
})

export const careNavigatorButtonLabel = defineMessage({
  defaultMessage: 'Have questions? Your Care Navigator is here for you 24/7. <link>Get in touch</link>',
  description: 'Explanation for why no dropdown options are shown in the onsite provider booking flow',
})

export const orLabel = defineMessages({
  LOWERCASE: {
    defaultMessage: 'or',
    description: 'The word "or"',
  },
  UPPERCASE: {
    defaultMessage: 'OR',
    description: 'The word "or" capitalized',
  },
})

export const resendCodeButtonLabel = defineMessage({
  defaultMessage: 'Resend code',
  description: 'Label of button to resend verification code',
})

export const notificationsOptInSuccessMessage = defineMessage({
  defaultMessage: 'You’re all set! You’ll now receive notifications.',
  description: 'Success message when user opts in to notifications',
})

export const invalidMFACodeErrorMessage = defineMessage({
  defaultMessage: 'We could not verify the code.',
  description: 'Error message for invalid code',
})

export const authenticatorMFASignInHelpText = defineMessage({
  defaultMessage:
    '{customerName} requires 2-factor authentication to be enabled for login. Enter the code from your authenticator application. If you need help, contact {email}.',
  description: 'Help text for sign in using authenticator app MFA',
})

export const authenticatorMFASignInHelpTextLink = defineMessage({
  defaultMessage:
    '{hasCustomerName, select, yes {{customerName}} other {Your employer}} requires 2-factor authentication to be enabled for login. Enter the code from your authenticator application. If you need help, contact <link>care@lyrahealth.com</link>.',
  description: 'Help text for sign in using authenticator app MFA',
})

export const authenticatorMFASetUpSuccessMessage = defineMessage({
  defaultMessage: 'Set up complete! You can now log in using your authenticator app.',
  description: 'Success message after user has completed setting up authenticator app MFA',
})

export const emailMFASetUpSuccessMessage = defineMessage({
  defaultMessage: 'Set up complete! You can now log in using email verification.',
  description: 'Success message after user has completed setting up email MFA',
})

export const dbtLoginSubtitle = defineMessage({
  defaultMessage: 'Use the email associated with your Dialectical Behavior Therapy (DBT) account.',
  description: 'Subtitle on login page for DBT customers explaining which email to use',
})

export const coverageBenefitsMessages = defineMessages({
  ANNUAL_BENEFITS_TITLE: {
    defaultMessage: 'Annual Lyra benefits {hasCustomerName, select, true {for {customerName}} other {}}',
    description: 'Coverage benefits title',
  },
  CUSTOMER_OFFERS_EVERY_YEAR_TITLE: {
    defaultMessage: 'Here’s what {customerName} offers every year',
    description: 'Coverage benefits title',
  },
})
